import React, { useEffect, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { useHistory } from 'react-router-dom';
import { Button, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, FormControlLabel, Grid, Select, MenuItem, InputLabel, FormControl, Link, Radio, RadioGroup, Checkbox } from '@mui/material';
import { ServiceStatusEnum, TokensVService as TokensV2Service } from '../../services/V2/gozer';
import { CreditCardTypeEnum, PaymentChannel, TokenType, TokensVService } from '../../services/V3/gozer';
import { checkPaymentAmount, formatMoney, handleError } from '../Shared/Functions';
import { withToast } from '../Shared/Toast';
import CCPayment from './CCPayment';
import ACHPayment from './ACHPayment';
import PaymentReceipt from './PaymentReceipt';
import DOMPurify from 'dompurify';
import { PaymentsVService, PaymentTypeEnum } from '../../services/V3/longhorn';

const PaymentPage = ({ successToastPersist, errorToast, account }) => {
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [subloading, setSubloading] = useState(false);
    const [tokens, setTokens] = useState<any[]>([]);
    const [tokenId, setTokenId] = useState<string>();
    const [payment, setPayment] = useState<any>();
    const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [canAcceptCc, setCanAcceptCc] = useState<boolean>(false);
    const [canAcceptAch, setCanAcceptAch] = useState<boolean>(false);
    const [paymentMode, setPaymentMode] = useState<string | null>(null);
    const [showConfirmOverPayment, setShowConfirmOverPayment] = useState<boolean>(false);
    const [maxExceeded, setMaxExceeded] = useState<boolean>(false);
    const [minExceeded, setMinExceeded] = useState<boolean>(false);
    const [showTermsAndConditions, setShowTermsAndConditions] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [showReceipt, setShowReceipt] = useState<boolean>(false);
    const [transaction, setTransaction] = useState<any>(null);
    const [errorDetails, setErrorDetails] = useState<any>(null);
    const [inactiveAccount, setInactiveAAccount] = useState<boolean>(false);
    const [paymentValue, setPaymentValue] = useState<any>("full");
    const [paymentMethod, setPaymentMethod] = useState<any>("primary");
    const [primaryMethod, setPrimaryMethod] = useState<any>(null);
    const [hasError, setHasError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<any>(null);
    const [zeroPayment, setZeroPayment] = useState<boolean>(false);
    const [ePaymentAllowed, setEPaymentAllowed] = useState<boolean>(true);
    const [fee, setFee] = useState<any>(0);
    const [feeError, setFeeError] = useState<boolean>(false);
    const [existingToken, setExistingToken] = useState<boolean>(true);
    const [clicked, setClicked] = useState<boolean>(false);


    useEffect(() => {
        setLoading(true)
        if (account) {
            let info = account.paymentInfo;
            setCanAcceptCc(info?.cc?.allowPayment ?? false);
            setCanAcceptAch(info?.ach?.allowPayment ?? false);
            setEPaymentAllowed(info?.ePaymentAllowed ?? false)
            setPayment({ ...payment, paymentAmount: account?.amountDue?.toString() });
            setPaymentValue("full");
            setInactiveAAccount(account.amountDue === 0 &&
                (account.serviceStatus === ServiceStatusEnum.NEW_FINAL ||
                    account.serviceStatus === ServiceStatusEnum.NEW_TRANSFER ||
                    account.serviceStatus === ServiceStatusEnum.OLD_FINAL ||
                    account.serviceStatus === ServiceStatusEnum.OLD_TRANSFER))

            TokensV2Service.tokensVGetAll(account.id)
                .then(x => {
                    let acceptableTokens = x.filter(y => !y.isThirdParty && ((y.tokenType === TokenType.ACH && !account.disallowACHPayments && info.ach.allowPayment) ||
                        (
                            y.tokenType === TokenType.CC && !account.disallowCCPayments && info.cc.allowPayment && (
                                (y.cardType === CreditCardTypeEnum.AMERICAN_EXPRESS && info.cc.acceptAmex) ||
                                (y.cardType === CreditCardTypeEnum.DISCOVER && info.cc.acceptDiscover) ||
                                (y.cardType === CreditCardTypeEnum.MASTER_CARD && info.cc.acceptMasterCard) ||
                                (y.cardType === CreditCardTypeEnum.VISA && info.cc.acceptVisa)
                            )
                        )))
                    setTokens(acceptableTokens);
                    setTokenId(acceptableTokens.find(y => y.isPrimary)?.id ? acceptableTokens.find(y => y.isPrimary)?.id : "")
                    setPrimaryMethod(acceptableTokens.find(y => y.isPrimary))
                    setPaymentMethod(acceptableTokens.find(y => y.isPrimary) ? "primary" : null)
                    setLoading(false)
                }).catch(err => err.message !== "Not Found" && errorToast(handleError(err)))
        }
    }, [account])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (tokenId === "NewCc" || paymentMethod === "CC" || tokens?.find(x => x.id === tokenId)?.tokenType === TokenType.CC) {
            setPaymentMode("CC")
        }
        else if (tokenId === "NewAch" || paymentMethod === "ACH" || tokens?.find(x => x.id === tokenId)?.tokenType === TokenType.ACH) {
            setPaymentMode("ACH")
        }
        else setPaymentMode(null)
    }, [tokenId])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        checkPaymentAmount(payment?.paymentAmount, paymentMode,
            account?.paymentInfo.cc.maxAmount, account?.paymentInfo.cc.minAmount,
            account?.paymentInfo.ach.maxAmount, account?.paymentInfo.ach.minAmount,
            setHasError, setErrorMessage);
    }, [payment, account, paymentMode])// eslint-disable-line react-hooks/exhaustive-deps

    const calculateFee = (newMethod) => {
        let token = tokens.find(x => x.id === tokenId);
        let districtId = account.districtId;
        let paymentType = paymentMode === "CC" ? PaymentTypeEnum.CC : PaymentTypeEnum.ACH;
        let cardType = token?.cardType ?? newMethod?.cardType;

        setSubloading(true)
        PaymentsVService.paymentsVGetServiceFee(districtId, paymentType, payment?.paymentAmount, cardType)
            .then(x => {
                setFee(x)
                setConfirmSubmit(true)
                setSubloading(false)
            }).catch(err => {
                if (handleError(err) === "No Convenience Fees Found") {
                    setFee(0)
                    setConfirmSubmit(true)
                    setSubloading(false)
                }
                else {
                    err.message !== "Not Found" && errorToast(handleError(err))
                    setFeeError(true);
                    setIsSubmitting(false);
                    setSubloading(false);
                }
            })
    }

    let iFrameAdvance = (token) => {
        setExistingToken(false)
        setTokens([...tokens, token]);
        setTokenId(token.id)
        if (payment?.paymentAmount > account.paymentInfo.cc.maxAmount) {
            setMaxExceeded(true)
            return;
        }
        if (payment?.paymentAmount < account.paymentInfo.cc.minAmount) {
            setMinExceeded(true)
            return;
        }
        if (payment?.paymentAmount <= 0) {
            setZeroPayment(true)
            return;
        }

        setIsSubmitting(true);

        if (payment?.paymentAmount > (account?.amountDue ?? 0)) {
            setShowConfirmOverPayment(true);
        } else {
            calculateFee(token);
        }
    }

    let cancel = () => {
        setConfirmSubmit(false);
        setIsSubmitting(false)
    }

    let paymentNotAllowedClose = () => {
        history.replace("/account");
    }

    let cancelOverPayment = () => {
        setShowConfirmOverPayment(false);
        setIsSubmitting(false);
    }

    let continueOverPayment = () => {
        setShowConfirmOverPayment(false);
        calculateFee(null);
    }

    let presubmit = (ach) => {
        if (tokenId === "NewAch" && ach) {
            setExistingToken(false)
            setTokenId(ach.id)
            setTokens([...tokens, ach])
        }
        if (paymentMode === "CC") {
            if (payment?.paymentAmount > account.paymentInfo.cc.maxAmount) {
                setMaxExceeded(true)
                return;
            }
            if (payment?.paymentAmount < account.paymentInfo.cc.minAmount) {
                setMinExceeded(true)
                return;
            }
        } else {
            if (payment?.paymentAmount > account.paymentInfo.ach.maxAmount) {
                setMaxExceeded(true)
                return;
            }
            if (payment?.paymentAmount < account.paymentInfo.ach.minAmount) {
                setMinExceeded(true)
                return;
            }
        }
        setIsSubmitting(true);

        if (payment?.paymentAmount > (account?.amountDue ?? 0)) {
            setShowConfirmOverPayment(true);
        } else {
            calculateFee(ach);
        }
    }

    const submit = () => {
        setIsProcessing(true)
        setLoading(true)
        TokensVService.tokensVCharge(account.id, tokenId ?? "", {
            channel: PaymentChannel.SIGNED_IN_PORTAL,
            amount: payment.paymentAmount
        })
            .then(x => {
                setErrorDetails(null);
                setTransaction(x);
                setShowReceipt(true)
                successToastPersist(`Confirmation Number: ${x?.confirmationNumber}.\nPayment Amount: ${formatMoney(x.amount)}.\nService Fee: ${formatMoney(x.serviceFeeAmount)}`);
                setIsProcessing(false)
                setIsSubmitting(false)
                setConfirmSubmit(false)
                setLoading(false)
            })
            .catch(err => {
                setErrorDetails(handleError(err));
                setTransaction({
                    amount: payment?.paymentAmount,
                    serviceFeeAmount: fee,
                    token: tokens.find(x => x.id === tokenId),
                    createdAt: new Date()
                });
                setShowReceipt(true)
                errorToast(handleError(err))
                setIsProcessing(false)
                setIsSubmitting(false)
                setConfirmSubmit(false)
                setLoading(false)
            })
    }

    const submitAch = (achToken) => {
        if (achToken) {
            presubmit(achToken)
            setLoading(false)
        }
    }

    const sanitizedTerms = (terms) => ({
        __html: DOMPurify.sanitize(terms),
    });

    return <>
        {/* Modals */}
        {showReceipt &&
            <PaymentReceipt
                transaction={transaction}
                setIsShowing={setShowReceipt}
                districtName={account?.districtInfo?.name}
                accountNumber={account?.accountNumber}
                accountName={account?.customer.fullName}
                errorDetails={errorDetails}
                isAnon={false}
                existingToken={existingToken}
                setLoading={setLoading}
                success={successToastPersist}
                fail={errorToast} />
        }
        {minExceeded &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Minimum payment amount not reached</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">
                        {paymentMode === "CC" ?
                            `We’re sorry, but the minimum Credit Card payment allowed is ${formatMoney(account.paymentInfo?.cc.minAmount)}.  Please try again.` :
                            `We’re sorry, but the minimum ACH payment allowed is ${formatMoney(account.paymentInfo?.ach.minAmount)}.  Please try again.`
                        }
                    </p>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="contained"
                            onClick={() => {
                                setMinExceeded(false)
                                setConfirmSubmit(false)
                            }}
                            className="m-2">Back</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {maxExceeded &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Maximum payment amount exceeded</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">
                        {paymentMode === "CC" ?
                            `We’re sorry, but the maximum Credit Card payment allowed is ${formatMoney(account.paymentInfo?.cc.maxAmount)}.  Please try again.` :
                            `We’re sorry, but the maximum ACH payment allowed is ${formatMoney(account.paymentInfo?.ach.maxAmount)}.  Please try again.`
                        }
                    </p>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="contained"
                            onClick={() => {
                                setMaxExceeded(false)
                                setConfirmSubmit(false)
                            }}
                            className="m-2">Back</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {zeroPayment &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Payment not allowed</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">
                        Amount must be greater than zero.  Please try again.
                    </p>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="contained"
                            onClick={() => {
                                setZeroPayment(false)
                                setConfirmSubmit(false)
                            }}
                            className="m-2">Back</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {showConfirmOverPayment &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Overpayment Confirmation</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">You requested to pay {formatMoney(payment?.paymentAmount)}, but the amount due is only: {formatMoney(account?.amountDue ?? 0)}. Do you want to proceed?</p>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="outlined"
                            onClick={cancelOverPayment}
                            className="m-2"
                            color="primary"
                            style={{ marginRight: "10px" }}>Cancel</Button>
                        <Button variant="contained"
                            onClick={continueOverPayment}
                            className="m-2">
                            Proceed
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {(!(canAcceptCc || canAcceptAch) && !inactiveAccount && ePaymentAllowed) &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Account Notice</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">
                        We’re sorry, online payments are not allowed for this account.
                        Please contact your utility office
                    </p>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="contained"
                            onClick={paymentNotAllowedClose}
                            className="m-2">Back To Account</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {!ePaymentAllowed &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Payment Notice</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">
                        This utility provider is unable to accept payments.
                        Please contact Customer Service. We apologize for any inconvenience.
                    </p>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="contained"
                            color="primary"
                            onClick={() => history.push("/sign-in")}
                            className="m-2">Back To Account</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {inactiveAccount &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Account Notice</DialogTitle>
                <DialogContent>
                    <p className="p-3 text-centered">
                        <b>Payment Restricted - Inactive Account Status</b>
                    </p>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="contained"
                            onClick={paymentNotAllowedClose}
                            className="m-2">Back To Account</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {showTermsAndConditions &&
            <Dialog
                fullWidth
                onClose={() => setShowTermsAndConditions(false)}
                open={true}>
                <DialogTitle>Terms and Conditions</DialogTitle>
                <DialogContent>
                    {(paymentMode === "CC" && account.termsAndConditions?.ccTermsAndConditions) ||
                        (paymentMode === "ACH" && account.termsAndConditions?.achTermsAndConditions) ? (
                        <div
                            dangerouslySetInnerHTML={paymentMode === "CC"
                                ? sanitizedTerms(account.termsAndConditions.ccTermsAndConditions)
                                : sanitizedTerms(account.termsAndConditions.achTermsAndConditions)}
                            style={{ fontSize: "16px" }}
                        />
                    ) : (
                        <div style={{ fontSize: "16px", color: "#b71c1c" }}>
                            Terms and Conditions are not available.
                        </div>
                    )}
                    <div className='payment-modal-buttons-group'>
                        <Button variant="contained"
                            onClick={() => setShowTermsAndConditions(false)}
                            className="m-2">Ok</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }
        {confirmSubmit &&
            <Dialog
                onClose={() => { }}
                open={true}>
                <DialogTitle>Confirm Payment</DialogTitle>
                <DialogContent>
                    <Grid container>
                        <Grid item xs={6} md={5}>
                            <b>Payment Amount</b>
                        </Grid>
                        <Grid item xs={6} md={5}>
                            {formatMoney(Number.parseFloat(payment?.paymentAmount)?.toFixed(2))}
                        </Grid>
                        <Grid item xs={0} md={2}></Grid>
                        <Grid item xs={6} md={5}>
                            <b>Service Fee</b>
                        </Grid>
                        <Grid item xs={6} md={5}>
                            {formatMoney(fee)}
                        </Grid>
                        <Grid item xs={0} md={2}></Grid>
                        <Grid item xs={6} md={5}>
                            <b>Total</b>
                        </Grid>
                        <Grid item xs={6} md={5}>
                            {formatMoney((Number.parseFloat(payment?.paymentAmount) + fee))}
                        </Grid>
                    </Grid>
                    {/*Terms and Conditions*/}
                    <div style={{ marginTop: "16px", textAlign: "center" }}>
                        <p>
                            <Checkbox
                                checked={clicked}
                                onChange={x => setClicked(x.target.checked)}
                                color="primary" />
                            By checking this box and completing this electronic payment, you agree to our{' '}
                            <Link
                                component="button"
                                onClick={() => setShowTermsAndConditions(true)}
                                className="clickable"
                                underline="always"
                                style={{ color: "#1976d2" }}
                            >
                                Terms and Conditions
                            </Link>
                        </p>
                    </div>
                    <div className='payment-modal-buttons-group'>
                        <Button variant="outlined"
                            color="primary"
                            disabled={isProcessing}
                            onClick={cancel}
                            className="m-2"
                            style={{ marginRight: "10px " }}>Back</Button>
                        <Button variant="contained"
                            disabled={isProcessing || feeError || !clicked}
                            onClick={submit}
                            className="m-2">Submit</Button>
                    </div>
                </DialogContent>
            </Dialog>
        }

        {/* Main User Interface */}
        {loading ?
            <div className='loading-container'><CircularProgress color="primary" className='center-spinner' aria-label="Loading" /></div>
            :
            <Grid container style={{ backgroundColor: "white", padding: "25px" }}>
                <Grid item xs={12} className='make-payment-grid'>
                    <Grid item className='payment-info'>
                        <div><b>Payment Date</b>&nbsp;&nbsp;&nbsp;&nbsp;{new Date().toLocaleDateString()}</div>
                        <br />
                        <div><b>Payment Amount</b></div>
                        <FormControl>
                            <RadioGroup
                                aria-label="Select payment amount"
                                name="payment-amount-group"
                                value={paymentValue}
                                onChange={(e) => {
                                    if (e.target.value === "full") {
                                        setPayment({
                                            ...payment,
                                            paymentAmount: account?.amountDue
                                        })
                                    } else if (e.target.value === "pastDue") {
                                        setPayment({
                                            ...payment,
                                            paymentAmount: account?.pastDueAmount
                                        })
                                    } else if (e.target.value === "other") {
                                        setPayment({
                                            ...payment,
                                            paymentAmount: null
                                        })
                                    }
                                    setPaymentValue(e.target.value);
                                }}>
                                <FormControlLabel defaultChecked value={"full"} control={<Radio />} label={`Total Amount Due ${formatMoney(account?.amountDue ?? 0)}`} />
                                {account?.pastDueAmount > 0 &&
                                    <FormControlLabel value={"pastDue"} control={<Radio />} label={`Past Due Amount ${formatMoney(account?.pastDueAmount ?? 0)}`} />
                                }
                                <FormControlLabel value="other" control={<Radio />} label="Other Amount" />
                            </RadioGroup>
                            {paymentValue === "other" &&
                                <div className="MuiFormControl-root MuiFormControl-fullWidth MuiTextField-root css-17vbkzs-MuiFormControl-root-MuiTextField-root" style={{ marginTop: "8px", marginBottom: "16px" }}>
                                    <div className="MuiInputBase-root MuiOutlinedInput-root MuiInputBase-colorPrimary MuiInputBase-fullWidth MuiInputBase-formControl css-decq32-MuiInputBase-root-MuiOutlinedInput-root" style={{ border: '1px solid #595959', borderRadius: '4px', display: 'flex', alignItems: 'center' }}>
                                        <CurrencyInput
                                            id="paymentAmount"
                                            className="payment-amount-input"
                                            style={{ fontSize: "1rem", padding: "6px 14px", width: "100%", boxSizing: "border-box", border: "none", outline: "none", background: "transparent" }}
                                            prefix="$"
                                            decimalScale={2}
                                            value={payment?.paymentAmount}
                                            aria-label="Other amount to pay"
                                            autoComplete="off"
                                            onValueChange={(e, m, floatValue) => setPayment({ ...payment, paymentAmount: floatValue?.value })} />
                                    </div>
                                </div>
                            }
                            {errorMessage && errorMessage !== "" &&
                                <div className='error'>
                                    {errorMessage}
                                </div>
                            }
                        </FormControl>
                    </Grid>
                    <Divider className='divider' orientation='vertical' variant="middle" flexItem />
                    <Grid item className='payment-method' style={{ marginLeft: "25px" }}>
                        <div><b>Payment Method</b></div>
                        <FormControl>
                            <RadioGroup
                                aria-label="Select payment method"
                                name="payment-method-group"
                                value={paymentMethod}
                                onChange={(e: any) => {
                                    if (e.target.value === "primary") {
                                        setTokenId(primaryMethod.id)
                                        if (primaryMethod.tokenType === TokenType.CC) {
                                            setPaymentMode("CC")
                                        }

                                        else {
                                            setPaymentMode("ACH")
                                        }
                                    } else {
                                        setTokenId("")
                                        setPaymentMode(e.target.value)
                                    }
                                    setPaymentMethod(e.target.value)
                                }}>
                                {primaryMethod && (primaryMethod.tokenType === TokenType.CC ?
                                    <FormControlLabel defaultChecked value={"primary"} control={<Radio />} label={`Pay with ${primaryMethod.cardType} ****${primaryMethod.last4}`} />
                                    :
                                    <FormControlLabel defaultChecked value={"primary"} control={<Radio />} label={`Pay with Bank Account ****${primaryMethod.last4}`} />
                                )}
                                {canAcceptCc &&
                                    <FormControlLabel value={"CC"} control={<Radio />} label={`Pay with Credit Card`} />
                                }
                                {canAcceptAch &&
                                    <FormControlLabel value={"ACH"} control={<Radio />} label={`Pay with Bank Account`} />
                                }
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Grid item className='payment-form' style={{ marginRight: "25px" }}>
                        <div style={{ width: "100%" }}>
                            {paymentMethod === "CC" ?
                                <FormControl style={{ width: "100%" }}>
                                    <InputLabel id="cc-tokenId-label" htmlFor="cc-tokenId">Select/Add Method</InputLabel>
                                    <Select
                                        labelId="cc-tokenId-label"
                                        id="cc-tokenId"
                                        value={tokenId}
                                        label="Select/Add Method"
                                        onChange={e => setTokenId(e.target.value)}
                                        fullWidth
                                    >
                                        {tokens.filter(x => !x.isPrimary)?.sort(x => x.tokenType)?.map(token => token.tokenType === TokenType.CC &&
                                            <MenuItem key={token.id} value={token.id}>{token.cardType} ****{token.last4}</MenuItem>)}
                                        {canAcceptCc &&
                                            <MenuItem value="NewCc">New CC Payment Method</MenuItem>
                                        }

                                    </Select>
                                </FormControl>
                                : paymentMethod === "ACH" ?
                                    <FormControl style={{ width: "100%" }}>
                                        <InputLabel id="ach-tokenId-label" htmlFor="ach-tokenId">Select/Add Method</InputLabel>
                                        <Select
                                            labelId="ach-tokenId-label"
                                            id="ach-tokenId"
                                            value={tokenId}
                                            label="Select/Add Method"
                                            onChange={e => setTokenId(e.target.value)}
                                            fullWidth
                                        >
                                            {tokens.filter(x => !x.isPrimary)?.sort(x => x.tokenType)?.map(token => token.tokenType === TokenType.ACH &&
                                                <MenuItem key={token.id} value={token.id}>Bank Account ****{token.last4}</MenuItem>)}
                                            {canAcceptAch &&
                                                <MenuItem value="NewAch">New ACH Payment Method</MenuItem>
                                            }

                                        </Select>
                                    </FormControl>
                                    : null
                            }
                            {tokenId === "NewCc" ?
                                <>
                                    {canAcceptCc &&
                                        <CCPayment
                                            errorToast={errorToast}
                                            account={account}
                                            submitPayment={iFrameAdvance}
                                            tokenCreateError={() => {
                                                setLoading(true)
                                                setIsSubmitting(false)
                                            }}
                                            setPaymentMethod={setPaymentMethod} />
                                    }
                                </>
                                : tokenId === "NewAch" ?
                                    <>
                                        {canAcceptAch &&
                                            <ACHPayment
                                                errorToast={errorToast}
                                                account={account}
                                                tokenCreateError={() => {
                                                    setLoading(true)
                                                    setIsSubmitting(false)
                                                }}
                                                setPaymentMethod={setPaymentMethod}
                                                submitAch={submitAch}
                                            />
                                        }
                                    </>
                                    : <div />

                            }
                        </div>
                    </Grid>
                </Grid>
                <Grid item xs={12} className='make-payment-buttons-group'>
                    <Button variant="outlined"
                        disabled={isSubmitting}
                        color="primary"
                        onClick={() => history.push("/account")}>Cancel</Button>
                    {tokenId !== "NewCc" && tokenId !== "NewAch" && tokenId !== "" &&
                        <Button variant="contained"
                            disabled={(isSubmitting || hasError)}
                            id="submit"
                            color="primary"
                            type="submit"
                            onClick={presubmit}>Continue</Button>
                    }
                </Grid>
                {subloading &&
                    <div className='loading-container'><CircularProgress color="primary" className='center-spinner' aria-label="Loading" /></div>
                }
            </Grid>
        }
    </>
}
export default withToast(PaymentPage);